import { useState, useEffect, useCallback, useRef } from 'react';
import Sidebar from '../components/Sidebar';

// --- Type Definitions ---

interface GuestOrder {
  orderIdx: number;
  orderNo: string;
  orderedDate: string;
  eventDate: string;
  ownerVendorName: string;
  ownerManagerName: string;
  ownerContact: string;
  customerNames: string;
  title: string;
  totalAmount: number;
  status: string;
  vendorCategoryIdx?: number | null;
  vendorCategoryId?: number | null;
  partnerVendorCategoryIdx?: number | null;
  vendorCategoryName?: string;
  vendorCategory?: string | number;
}

const WEDDING_HALL_VENDOR_CATEGORY_IDX = 82;

// 상태 필터 옵션
const STATUS_OPTIONS = [
  { label: '전체', value: '' },
  { label: '발주완료', value: 'ORDERED' },
  { label: '역발주(확정)', value: 'REVERSE_ORDERED' },
  { label: '인보이스', value: 'INVOICED' },
  { label: '결제완료', value: 'SETTLED' },
  { label: '세금계산서', value: 'TAX_ISSUED' },
  { label: '거래완료', value: 'COMPLETED' },
  { label: '취소', value: 'CANCELLED' },
];

const getResponseMessage = async (response: Response) => {
  const payload = await response.json().catch(() => null);
  return payload?.message || payload?.data?.message || '';
};

const parseVendorCategoryIdx = (order: Partial<GuestOrder>): number | null => {
  const candidates = [
    order.vendorCategoryIdx,
    order.vendorCategoryId,
    order.partnerVendorCategoryIdx,
    order.vendorCategory,
  ];

  for (const candidate of candidates) {
    const parsed = Number(candidate);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }

  const categoryName = String(order.vendorCategoryName || order.vendorCategory || '').trim();
  if (categoryName === '웨딩홀') return WEDDING_HALL_VENDOR_CATEGORY_IDX;

  return null;
};

const resolveIsWeddingHallVendorUser = (): boolean => {
  try {
    const raw = localStorage.getItem('userInfo');
    if (!raw) return false;
    const user = JSON.parse(raw);

    const categoryIdxCandidates = [
      user?.vendorCategoryIdx,
      user?.vendor?.category?.vendorCategoryIdx,
      user?.selectedContextVendorCategoryIdx,
    ];
    const hasWeddingHallCategoryIdx = categoryIdxCandidates.some((candidate: unknown) => {
      const parsed = Number(candidate);
      return Number.isFinite(parsed) && parsed === WEDDING_HALL_VENDOR_CATEGORY_IDX;
    });
    if (hasWeddingHallCategoryIdx) return true;

    const categoryNameCandidates = [
      user?.vendorCategoryName,
      user?.categoryName,
      user?.vendor?.category?.name,
      user?.vendor?.categoryName,
    ];
    return categoryNameCandidates.some((candidate: unknown) => String(candidate || '').trim() === '웨딩홀');
  } catch {
    return false;
  }
};

export default function GuestOrderPage() {
  // --- State ---
  const [orders, setOrders] = useState<GuestOrder[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isWeddingHallVendorUser, setIsWeddingHallVendorUser] = useState(false);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Modals & Form Data
  const [selectedOrder, setSelectedOrder] = useState<GuestOrder | null>(null);
  
  // 1. 역발주 모달 State
  const [showReverseOrderModal, setShowReverseOrderModal] = useState(false);
  const [confirmData, setConfirmData] = useState({ price: 0, message: '' });

  // 2. 인보이스 모달 State
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceData, setInvoiceData] = useState({ 
    invoiceNo: '', 
    totalAmount: 0, 
    dueDt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0] // 기본 7일 후
  });

  // 3. 세금계산서 모달 State
  const [showTaxModal, setShowTaxModal] = useState(false);
  const [taxData, setTaxData] = useState({ 
    ntsIssueId: '', 
    supplyPrice: 0, 
    taxAmount: 0 
  });

  // ✅ [NEW] 드롭다운 메뉴 상태 관리
  const [activeDownloadMenuId, setActiveDownloadMenuId] = useState<number | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // 메뉴 외부 클릭 시 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setActiveDownloadMenuId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setIsWeddingHallVendorUser(resolveIsWeddingHallVendorUser());
  }, []);


  // --- API Calls ---

  // 목록 조회
  const fetchOrders = useCallback(async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const params = new URLSearchParams();
      params.append('page', '1');
      params.append('size', '50');
      if (searchTerm) params.append('q', searchTerm);
      if (statusFilter) params.append('status', statusFilter);

      const response = await fetch(`https://api.wedmin.biz/order/guest?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const message = await getResponseMessage(response);
        if (response.status === 403) {
          alert(message || '권한이 없습니다.');
          setOrders([]);
          return;
        }
        throw new Error(message || '데이터 로딩 실패');
      }

      const result = await response.json();
      const data = result.data || result; 
      
      if (data && Array.isArray(data.items)) {
        setOrders(data.items);
      } else {
        setOrders([]);
      }
    } catch (error) {
      console.error('Fetch Error:', error);
    } finally {
      setIsLoading(false);
    }
  }, [searchTerm, statusFilter]);

  useEffect(() => {
    const timer = setTimeout(() => fetchOrders(), 300);
    return () => clearTimeout(timer);
  }, [fetchOrders]);

  // ✅ [NEW] PDF 다운로드 통합 핸들러
  const handleDownloadPdf = async (orderIdx: number, type: 'ORDER' | 'REVERSE' | 'INVOICE' | 'WEDDING_HALL') => {
    setActiveDownloadMenuId(null); // 메뉴 닫기
    try {
      const token = localStorage.getItem('accessToken');
      let url = '';

      // 타입별 API 주소 분기
      if (type === 'INVOICE') {
        url = `https://api.wedmin.biz/order/${orderIdx}/invoice/download`;
      } else if (type === 'WEDDING_HALL') {
        url = `https://api.wedmin.biz/order/${orderIdx}/weddinghall/download`;
      } else if (type === 'REVERSE') {
        url = `https://api.wedmin.biz/order/${orderIdx}/confirmation/download`;
      } else {
        // 기본 발주서 (ORDER)
        url = `https://api.wedmin.biz/order/${orderIdx}/download?type=ORDER`;
      }

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const err = await response.json(); 
        throw new Error(err.message || '다운로드 실패');
      }

      const blob = await response.blob(); 
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      
      const filePrefix = {
        ORDER: '발주서',
        REVERSE: '역발주확인서',
        INVOICE: '청구서',
        WEDDING_HALL: '웨딩홀확인서'
      };
      
      link.setAttribute('download', `${filePrefix[type]}_ORD_${orderIdx}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);

    } catch (e: any) {
      console.error(e);
      alert(`문서 다운로드 중 오류: ${e.message}`);
    }
  };

  // ✅ [NEW] 상태별 다운로드 가능한 문서 목록 정의
  const getDownloadableDocs = (order: GuestOrder) => {
    // 1. 발주서 (항상 가능)
    const docs = [
      { type: 'ORDER', label: '발주서 (Order)' },
    ];

    const categoryIdx = parseVendorCategoryIdx(order);
    const isWeddingHallOrder = categoryIdx === WEDDING_HALL_VENDOR_CATEGORY_IDX;
    if (isWeddingHallOrder || isWeddingHallVendorUser) {
      docs.push({ type: 'WEDDING_HALL', label: '웨딩홀 확인서' });
    }

    // 2. 역발주서 (확정 이후 가능)
    const confirmStatuses = ['REVERSE_ORDERED', 'INVOICED', 'SETTLED', 'TAX_ISSUED', 'COMPLETED'];
    if (confirmStatuses.includes(order.status)) {
      docs.push({ type: 'REVERSE', label: '역발주확인서 (Confirm)' });
    }

    // 3. 인보이스 (발행 이후 가능)
    const invoiceStatuses = ['INVOICED', 'SETTLED', 'TAX_ISSUED', 'COMPLETED'];
    if (invoiceStatuses.includes(order.status)) {
      docs.push({ type: 'INVOICE', label: '청구서 (Invoice)' });
    }

    return docs;
  };

  // ① 역발주/확정 처리
  const handleConfirmOrder = async () => {
    if (!selectedOrder) return;
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`https://api.wedmin.biz/order/confirm/${selectedOrder.orderIdx}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          confirmedPrice: Number(confirmData.price) > 0 ? Number(confirmData.price) : Number(selectedOrder.totalAmount || 0),
          vendorMessage: confirmData.message,
          fileUrl: '' 
        })
      });

      if (response.ok) {
        alert('역발주(확정) 처리가 완료되었습니다.');
        setShowReverseOrderModal(false);
        fetchOrders();
      } else {
        const message = await getResponseMessage(response);
        if (response.status === 403) {
          alert(message || '권한이 없습니다.');
          return;
        }
        alert(`처리 실패: ${message || '오류가 발생했습니다.'}`);
      }
    } catch (e) {
      console.error(e);
      alert('오류가 발생했습니다.');
    }
  };

  // ② 인보이스 발행
  const handleIssueInvoice = async () => {
    if (!selectedOrder) return;
    const generatedInvoiceNo = invoiceData.invoiceNo || `INV-${Date.now()}`;

    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`https://api.wedmin.biz/order/invoice/${selectedOrder.orderIdx}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          invoiceNo: generatedInvoiceNo,
          totalAmount: Number(invoiceData.totalAmount),
          issueDt: new Date().toISOString().split('T')[0], 
          dueDt: invoiceData.dueDt,
          fileUrl: ''
        })
      });

      if (response.ok) {
        alert('인보이스가 발행되었습니다.');
        setShowInvoiceModal(false);
        fetchOrders();
      } else {
        const message = await getResponseMessage(response);
        if (response.status === 403) {
          alert(message || '권한이 없습니다.');
          return;
        }
        alert(`발행 실패: ${message || '오류가 발생했습니다.'}`);
      }
    } catch (e) {
      console.error(e);
      alert('오류가 발생했습니다.');
    }
  };

  // ③ 세금계산서 발행
  const handleIssueTaxInvoice = async () => {
    if (!selectedOrder) return;
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`https://api.wedmin.biz/order/tax-invoice/${selectedOrder.orderIdx}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          orderIdx: selectedOrder.orderIdx,
          ntsIssueId: taxData.ntsIssueId,
          totalSupplyAmount: Number(taxData.supplyPrice),
          totalTaxAmount: Number(taxData.taxAmount),
          issueDt: new Date().toISOString().split('T')[0],
          fileUrl: '',
          isReported: true 
        })
      });

      if (response.ok) {
        alert('세금계산서 정보가 등록되었습니다.');
        setShowTaxModal(false);
        fetchOrders();
      } else {
        const message = await getResponseMessage(response);
        if (response.status === 403) {
          alert(message || '권한이 없습니다.');
          return;
        }
        alert(`등록 실패: ${message || '오류가 발생했습니다.'}`);
      }
    } catch (e) {
      console.error(e);
      alert('오류가 발생했습니다.');
    }
  };

  // --- Modal Open Handlers ---
  const openReverseOrderModal = (order: GuestOrder) => {
    setSelectedOrder(order);
    setConfirmData({ price: order.totalAmount, message: '' });
    setShowReverseOrderModal(true);
  };

  const openInvoiceModal = (order: GuestOrder) => {
    setSelectedOrder(order);
    setInvoiceData({ 
      invoiceNo: `INV-${new Date().getFullYear()}-${Math.floor(Math.random()*1000)}`,
      totalAmount: order.totalAmount, 
      dueDt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
    });
    setShowInvoiceModal(true);
  };

  const openTaxModal = (order: GuestOrder) => {
    setSelectedOrder(order);
    const supply = Math.round(order.totalAmount / 1.1);
    setTaxData({ 
      ntsIssueId: '', 
      supplyPrice: supply, 
      taxAmount: order.totalAmount - supply 
    });
    setShowTaxModal(true);
  };

  // --- UI Helpers ---
  const getStatusLabel = (status: string) => {
    const found = STATUS_OPTIONS.find(o => o.value === status);
    return found ? found.label : status;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ORDERED': return 'bg-blue-100 text-blue-800';
      case 'REVERSE_ORDERED': return 'bg-yellow-100 text-yellow-800';
      case 'INVOICED': return 'bg-orange-100 text-orange-800';
      case 'SETTLED': return 'bg-green-100 text-green-800';
      case 'TAX_ISSUED': return 'bg-indigo-100 text-indigo-800';
      case 'COMPLETED': return 'bg-gray-100 text-gray-800';
      case 'CANCELLED': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />

      <div className="flex-1 p-8 overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">수주(발주) 관리</h1>
            <p className="text-sm text-gray-600 mt-1">파트너사로부터 요청받은 주문 내역을 관리합니다.</p>
          </div>
        </div>

        {/* 검색 및 필터 */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex-1 min-w-64">
              <input
                type="text"
                placeholder="검색..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-4 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm"
              />
            </div>
            <div className="flex gap-2 overflow-x-auto">
              {STATUS_OPTIONS.map((option) => (
                <button
                  key={option.label}
                  onClick={() => setStatusFilter(option.value)}
                  className={`px-4 py-2 text-sm font-medium rounded-lg whitespace-nowrap ${
                    statusFilter === option.value ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* 목록 */}
        {isLoading ? (
          <div className="flex justify-center p-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map((order) => (
              <div key={order.orderIdx} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 relative"> {/* relative 추가 */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <i className="ri-file-text-line text-blue-600 text-xl"></i>
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900">{`ORD-${order.orderIdx}`}</h3>
                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                          {order.orderedDate} 발주
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mt-0.5">
                        {order.title} | 발주사: <span className="font-medium text-gray-800">{order.ownerVendorName}</span>
                      </p>
                    </div>
                  </div>
                  
                  {/* 상태 배지 및 다운로드 버튼 그룹 */}
                  <div className="flex items-center gap-2">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                      {getStatusLabel(order.status)}
                    </span>

                    {/* ✅ [NEW] 다운로드 드롭다운 메뉴 */}
                    <div className="relative">
                      <button
                        onClick={(e) => { 
                          e.stopPropagation(); 
                          setActiveDownloadMenuId(activeDownloadMenuId === order.orderIdx ? null : order.orderIdx);
                        }}
                        className={`p-1.5 rounded-full transition-colors ${
                          activeDownloadMenuId === order.orderIdx ? 'bg-blue-100 text-blue-600' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                        }`}
                        title="문서 다운로드"
                      >
                        <i className="ri-download-line text-lg"></i>
                      </button>

                      {/* 드롭다운 본문 */}
                      {activeDownloadMenuId === order.orderIdx && (
                        <div 
                          ref={menuRef}
                          className="absolute right-0 top-full mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-200 z-50 overflow-hidden"
                          onClick={(e) => e.stopPropagation()} 
                        >
                          <div className="py-1">
                            <div className="px-3 py-2 border-b border-gray-100 bg-gray-50 text-xs font-semibold text-gray-500">
                              다운로드 가능한 문서
                            </div>
                            {getDownloadableDocs(order).map((doc) => (
                              <button
                                key={doc.type}
                                onClick={() => handleDownloadPdf(order.orderIdx, doc.type as any)}
                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 flex items-center justify-between group"
                              >
                                <span>{doc.label}</span>
                                <i className="ri-file-download-line opacity-0 group-hover:opacity-100 text-blue-500"></i>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 bg-gray-50 p-4 rounded-lg">
                  <div><p className="text-xs text-gray-500">고객명</p><p className="text-sm font-medium">{order.customerNames}</p></div>
                  <div><p className="text-xs text-gray-500">행사일</p><p className="text-sm font-medium">{order.eventDate}</p></div>
                  <div><p className="text-xs text-gray-500">총 금액</p><p className="text-sm font-medium text-blue-600">{order.totalAmount.toLocaleString()}원</p></div>
                  <div><p className="text-xs text-gray-500">담당자</p><p className="text-sm font-medium">{order.ownerManagerName}</p></div>
                </div>

                <div className="flex items-center justify-end gap-2 pt-4 border-t border-gray-100">
                  {order.status === 'ORDERED' && (
                    <button onClick={() => openReverseOrderModal(order)} className="bg-yellow-600 text-white px-4 py-2 rounded-lg hover:bg-yellow-700 text-sm">역발주 작성</button>
                  )}
                  {order.status === 'REVERSE_ORDERED' && (
                    <button onClick={() => openInvoiceModal(order)} className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 text-sm">인보이스 발행</button>
                  )}
                  {(order.status === 'SETTLED') && (
                    <button onClick={() => openTaxModal(order)} className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 text-sm">세금계산서 발행 완료</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* --- Modals --- */}
      {/* 1. 역발주 작성 모달 */}
      {showReverseOrderModal && selectedOrder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6">
            <h2 className="text-xl font-bold mb-4">역발주(확정) 작성</h2>
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded text-sm text-gray-600">
                <p>주문번호: {selectedOrder.orderNo}</p>
                <p>고객: {selectedOrder.customerNames}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">
                  확정 금액은 주문 금액 기준으로 자동 전송됩니다.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">비고 (수행사 메시지)</label>
                <textarea 
                  value={confirmData.message}
                  onChange={(e) => setConfirmData({...confirmData, message: e.target.value})}
                  className="w-full border rounded px-3 py-2" rows={3} 
                />
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <button onClick={() => setShowReverseOrderModal(false)} className="px-4 py-2 bg-gray-200 rounded">취소</button>
                <button onClick={handleConfirmOrder} className="px-4 py-2 bg-yellow-600 text-white rounded">전송</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 2. 인보이스 발행 모달 */}
      {showInvoiceModal && selectedOrder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6">
            <h2 className="text-xl font-bold mb-4">인보이스 발행</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">인보이스 번호</label>
                <input 
                  type="text" 
                  value={invoiceData.invoiceNo} 
                  onChange={(e) => setInvoiceData({...invoiceData, invoiceNo: e.target.value})}
                  className="w-full border rounded px-3 py-2" 
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">청구 금액</label>
                <input 
                  type="number" 
                  value={invoiceData.totalAmount} 
                  onChange={(e) => setInvoiceData({...invoiceData, totalAmount: Number(e.target.value)})}
                  className="w-full border rounded px-3 py-2" 
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">입금 기한</label>
                <input 
                  type="date" 
                  value={invoiceData.dueDt} 
                  onChange={(e) => setInvoiceData({...invoiceData, dueDt: e.target.value})}
                  className="w-full border rounded px-3 py-2" 
                />
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <button onClick={() => setShowInvoiceModal(false)} className="px-4 py-2 bg-gray-200 rounded">취소</button>
                <button onClick={handleIssueInvoice} className="px-4 py-2 bg-orange-600 text-white rounded">발행</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 3. 세금계산서 모달 */}
      {showTaxModal && selectedOrder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6">
            <h2 className="text-xl font-bold mb-4">세금계산서 발행</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">국세청 승인번호</label>
                <input 
                  type="text" 
                  placeholder="승인번호 입력" 
                  value={taxData.ntsIssueId}
                  onChange={(e) => setTaxData({...taxData, ntsIssueId: e.target.value})}
                  className="w-full border rounded px-3 py-2" 
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-sm font-medium mb-1">공급가액</label>
                  <input 
                    type="number" 
                    value={taxData.supplyPrice} 
                    onChange={(e) => setTaxData({...taxData, supplyPrice: Number(e.target.value)})}
                    className="w-full border rounded px-3 py-2" 
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">세액</label>
                  <input 
                    type="number" 
                    value={taxData.taxAmount} 
                    onChange={(e) => setTaxData({...taxData, taxAmount: Number(e.target.value)})}
                    className="w-full border rounded px-3 py-2" 
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <button onClick={() => setShowTaxModal(false)} className="px-4 py-2 bg-gray-200 rounded">취소</button>
                <button onClick={handleIssueTaxInvoice} className="px-4 py-2 bg-indigo-600 text-white rounded">등록</button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
